import { useState, useEffect, useRef, useCallback } from 'react';
import { webOrigin } from '../utils/brand';
import { API_BASE } from '../utils/apiBase';
import { useSearchParams } from 'react-router-dom';
import { ClaimCard } from '../components/pro/vendor/VendorOfferCard';
import { vendorService } from '../services';
import { useNavigate } from 'react-router-dom';
import { Navbar, LoadingSpinner } from '../components';
import { ticketService } from '../services';
import gatheringService from '../services/gatheringService';
import { seatQrUrl } from './GatheringTicketPage';
import { useAuth, useUser, useDialog } from '../context';

/**
 * MyTicketsPage — every ticket you hold, grouped by the night it gets you into.
 *
 * A flat grid was the wrong shape: five tickets to one party read as five parties.
 * Each event is one block now — poster, date, city, how many you're holding — and
 * its tickets sit in a horizontal rail underneath that you swipe on a phone, the
 * same gesture as the deck on the ticket page itself. On a desktop, where there's
 * nothing to swipe with, arrows appear at either end of a rail that overflows.
 *
 * Groups key on event id, not title: two parties can share a name, and every
 * ticket whose event row is gone renders as "Event removed" — grouping those on
 * the label would pile unrelated tickets into one block.
 */
export default function MyTicketsPage() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { profile, walletBalance } = useUser();
  const [tickets, setTickets] = useState([]);
  // Native gathering seats (Oct 2026) — their own rows, never folded into event groups.
  const [seats, setSeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sendingTicketId, setSendingTicketId] = useState(null);
  // Only one transfer panel open at a time — they're long, and two open in one
  // rail pushes the card you were looking at off-screen.
  const [transferTicketId, setTransferTicketId] = useState(null);
  const [sendForm, setSendForm] = useState({ email: '', recipientName: '' });
  const [sendError, setSendError] = useState('');
  const [sendSuccess, setSendSuccess] = useState('');
  const [wallet, setWallet] = useState({ applePass: false, googlePass: false });
  // Past nights stay folded away — they're the bulk of a long-standing account
  // and none of them get you in anywhere.
  const [showPast, setShowPast] = useState(false);
  const apiBase = API_BASE;
  // Vendor offers the guest holds (Offers Ops O10): a second tab. A spent offer doesn't count.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'offers' ? 'offers' : 'tickets';
  const [claims, setClaims] = useState([]);
  const loadClaims = useCallback(async () => { const r = await vendorService.myClaims(); if (r.success) setClaims(r.data || []); }, []);
  useEffect(() => { loadClaims(); }, [loadClaims]);
  const liveClaims = claims.filter((c) => c.state === 'active');
  const spentClaims = claims.filter((c) => c.state !== 'active');
  const releaseClaim = async (c) => { const r = await vendorService.releaseClaim(c.id); if (r.success) loadClaims(); };

  useEffect(() => {
    fetchTickets();
    fetch(`${apiBase}/tickets/wallet/capabilities`)
      .then((r) => r.json())
      .then((d) => setWallet({ applePass: !!d?.data?.applePass, googlePass: !!d?.data?.googlePass }))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTickets = async () => {
    try {
      const response = await ticketService.getMyTickets();
      setTickets(response.data?.tickets || []);
    } catch (err) {
      setError('Failed to load tickets');
    } finally {
      setLoading(false);
    }
    gatheringService.myTickets().then((r) => { if (r?.success) setSeats(r.data || []); }).catch(() => {});
  };

  const handleSendTicket = async (ticketId) => {
    setSendError('');
    setSendSuccess('');
    try {
      await ticketService.sendTicket(ticketId, {
        email: sendForm.email,
        recipientName: sendForm.recipientName,
      });
      setSendSuccess('Ticket sent!');
      setSendingTicketId(null);
      setSendForm({ email: '', recipientName: '' });
      fetchTickets();
    } catch (err) {
      setSendError(err.response?.data?.error?.message || 'Failed to send ticket');
    }
  };

  const { upcoming, past } = splitByRecency(tickets);
  const upcomingTicketCount = upcoming.reduce((n, g) => n + g.tickets.length, 0);

  return (
    <div className="min-h-screen bg-black">
      <Navbar
        mode="guest"
        onModeChange={(mode) => navigate(mode === 'guest' ? '/guest' : mode === 'featured' ? '/featured' : '/host')}
        user={profile}
        walletBalance={walletBalance}
        onLogout={logout}
      />

      <div className="max-w-7xl mx-auto px-6 py-8">
        <h1 className="font-mono text-2xl font-bold uppercase tracking-wider text-white mb-2">
          MY TICKETS
        </h1>
        <p className="font-mono text-sm text-gray-500 mb-6">
          {tab === 'offers' ? 'Everything you’re holding, in one place.'
            : upcoming.length > 0
            ? `${upcomingTicketCount} ticket${upcomingTicketCount === 1 ? '' : 's'} across ${upcoming.length} upcoming event${upcoming.length === 1 ? '' : 's'}.`
            : tickets.length > 0
              ? 'Nothing coming up.'
              : 'All your event tickets in one place.'}
        </p>

        {/* TICKETS n / OFFERS n — counts live in the labels so a guest sees they're carrying something. */}
        <div className="mb-8 flex border-b-2 border-white" role="tablist" data-testid="wallet-tabs">
          {[['tickets', `TICKETS ${upcomingTicketCount}`], ['offers', `OFFERS ${liveClaims.length}`]].map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => { const n = new URLSearchParams(searchParams); if (k === 'offers') n.set('tab', 'offers'); else n.delete('tab'); setSearchParams(n); }}
              className="flex-1 text-center font-mono font-bold uppercase" style={{ padding: '13px 6px', fontSize: 13, letterSpacing: '0.05em', marginBottom: -2, borderBottom: `2px solid ${tab === k ? '#fff' : 'transparent'}`, color: tab === k ? '#fff' : '#6B7280' }}>{label}</button>
          ))}
        </div>

        {tab === 'offers' && (
          <div className="flex flex-col" style={{ gap: 12 }} data-testid="offers-tab">
            {claims.length === 0 && (
              <div className="border-2 border-white p-8 text-center">
                <p className="font-mono text-lg font-bold uppercase tracking-wider text-white mb-2">NOTHING CLAIMED YET</p>
                <p className="font-mono text-sm text-gray-500">Places on Portal post offers you claim here and carry in.</p>
                <button type="button" onClick={() => navigate('/pros?places=1')} className="mt-4 px-6 py-3 border-2 border-white text-white font-mono text-xs font-bold uppercase tracking-wider hover:bg-white hover:text-black transition-colors">FIND A PLACE</button>
              </div>
            )}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {liveClaims.map((c) => <ClaimCard key={c.id} claim={c} size={150} compact onRelease={releaseClaim} />)}
            </div>
            {spentClaims.map((c) => <ClaimCard key={c.id} claim={c} compact />)}
          </div>
        )}

        {tab === 'tickets' && (<>

        {loading && (
          <div className="flex justify-center py-16">
            <LoadingSpinner />
          </div>
        )}

        {error && (
          <div className="border-2 border-red-600 p-4 text-center">
            <p className="font-mono text-sm text-red-600">{error}</p>
          </div>
        )}

        {seats.length > 0 && <GatheringSeats seats={seats} navigate={navigate} />}

        {!loading && !error && tickets.length === 0 && seats.length === 0 && (
          <div className="border border-white/20 p-8 text-center">
            <p className="font-mono text-lg font-bold uppercase tracking-wider text-white mb-2">NO TICKETS YET</p>
            <p className="font-mono text-sm text-gray-500 mb-4">Purchase tickets to events to see them here.</p>
            <button
              type="button"
              onClick={() => navigate('/guest')}
              className="px-6 py-3 border border-white text-white font-mono text-xs font-bold uppercase tracking-wider hover:bg-white hover:text-black transition-colors"
            >
              BROWSE EVENTS
            </button>
          </div>
        )}

        {sendSuccess && (
          <div className="border border-green-500 p-3 mb-4">
            <p className="font-mono text-xs text-green-400 font-bold">{sendSuccess}</p>
          </div>
        )}

        {(() => {
          const groupProps = {
            wallet,
            navigate,
            sendingTicketId,
            setSendingTicketId,
            sendForm,
            setSendForm,
            sendError,
            setSendError,
            setSendSuccess,
            onSend: handleSendTicket,
            transferTicketId,
            setTransferTicketId,
            onTransferChanged: fetchTickets,
          };
          return (
            <>
              <div className="space-y-10">
                {upcoming.map((group) => (
                  <EventGroup key={group.key} group={group} {...groupProps} />
                ))}
              </div>

              {/* Everything already danced. Folded away by default, one click
                  from the whole history. */}
              {past.length > 0 && (
                <div className={upcoming.length > 0 ? 'mt-12' : ''}>
                  <button
                    type="button"
                    onClick={() => setShowPast((v) => !v)}
                    aria-expanded={showPast}
                    className="flex w-full items-center gap-4 border border-white/20 px-4 py-3 transition-colors hover:border-white/50"
                  >
                    <span className="whitespace-nowrap font-mono text-xs font-bold uppercase tracking-wider text-white">
                      Past events ({past.length})
                    </span>
                    <span className="h-px flex-1 bg-white/20" />
                    <span className="whitespace-nowrap font-mono text-[11px] uppercase tracking-wider text-gray-500">
                      {showPast ? 'Hide' : 'Show'}
                    </span>
                    <svg
                      width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor"
                      strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter"
                      aria-hidden="true"
                      className={`flex-shrink-0 text-gray-500 transition-transform ${showPast ? 'rotate-180' : ''}`}
                    >
                      <path d="M2 4l4 4 4-4" />
                    </svg>
                  </button>

                  {showPast && (
                    <div className="mt-6 space-y-10">
                      {past.map((group) => (
                        <EventGroup key={group.key} group={group} {...groupProps} />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          );
        })()}
        </>)}
      </div>
    </div>
  );
}

/**
 * One event's tickets in a horizontal rail. Swipe on a phone; on a pointer device
 * the arrows do the same job, and only appear when there's actually something off
 * screen to reach.
 */
function EventGroup({
  group, wallet, navigate,
  sendingTicketId, setSendingTicketId, sendForm, setSendForm, sendError, setSendError, setSendSuccess, onSend,
  transferTicketId, setTransferTicketId, onTransferChanged,
}) {
  const railRef = useRef(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(true);

  // Which arrows are worth showing. Re-measured on scroll and on resize, since a
  // rail that overflows on a phone may fit fine once the window is widened.
  const measure = useCallback(() => {
    const el = railRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setAtStart(el.scrollLeft <= 4);
    setAtEnd(el.scrollLeft >= max - 4);
  }, []);

  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure, group.tickets.length]);

  const nudge = (dir) => {
    const el = railRef.current;
    if (!el) return;
    // A card and its gap — one swipe's worth.
    el.scrollBy({ left: dir * Math.min(el.clientWidth * 0.85, 340), behavior: 'smooth' });
  };

  const overflows = !(atStart && atEnd);

  return (
    <section className="border border-white/20">
      {/* Event header — the thing all these tickets are for. */}
      <div className="flex items-center gap-4 border-b border-white/20 p-4">
        {group.eventPosterUrl && (
          <img
            src={group.eventPosterUrl}
            alt={group.eventTitle}
            className="h-16 w-16 flex-shrink-0 object-cover"
          />
        )}
        <div className="min-w-0 flex-1">
          <h2 className="truncate font-mono text-base font-bold uppercase tracking-wider text-white">
            {group.eventTitle}
          </h2>
          <p className="font-mono text-xs text-gray-400">
            {formatDate(group.eventDateTime)}
            {group.eventLocationCity ? ` · ${group.eventLocationCity}` : ''}
          </p>
        </div>
        <span className="flex-shrink-0 border border-white/40 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-white">
          {group.tickets.length} {group.tickets.length === 1 ? 'ticket' : 'tickets'}
        </span>
      </div>

      <div className="relative">
        <div
          ref={railRef}
          onScroll={measure}
          className="flex snap-x snap-mandatory gap-4 overflow-x-auto p-4"
          style={{ scrollbarWidth: 'none' }}
        >
          {group.tickets.map((ticket) => (
            <div
              key={ticket.id}
              className="w-[85%] max-w-[320px] flex-shrink-0 snap-start sm:w-[300px]"
            >
              <TicketCard
                ticket={ticket}
                wallet={wallet}
                navigate={navigate}
                transferTicketId={transferTicketId}
                setTransferTicketId={setTransferTicketId}
                onTransferChanged={onTransferChanged}
                sendingTicketId={sendingTicketId}
                setSendingTicketId={setSendingTicketId}
                sendForm={sendForm}
                setSendForm={setSendForm}
                sendError={sendError}
                setSendError={setSendError}
                setSendSuccess={setSendSuccess}
                onSend={onSend}
              />
            </div>
          ))}
        </div>

        {/* Pointer-device equivalent of the swipe. Hidden when it all fits. */}
        {overflows && (
          <>
            <RailArrow side="left" disabled={atStart} onClick={() => nudge(-1)} />
            <RailArrow side="right" disabled={atEnd} onClick={() => nudge(1)} />
          </>
        )}
      </div>

      {group.tickets.length > 1 && (
        <p className="border-t border-white/10 px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-gray-500 sm:hidden">
          Swipe for your other tickets
        </p>
      )}
    </section>
  );
}

/**
 * GATHERINGS — the viewer's native seats, grouped per gathering. A seat is entry to the
 * gathering only; the events on its agenda keep their own tickets above. Tapping a card opens
 * the seat (`/gt/{code}`) where the QR, the offer link and the status live.
 */
export function GatheringSeats({ seats, navigate }) {
  const groups = [];
  const byId = new Map();
  for (const s of seats) {
    if (!byId.has(s.gatheringId)) { const g = { key: s.gatheringId, name: s.gatheringName, startsAt: s.startsAt, endsAt: s.endsAt, coverUrl: s.coverUrl, clubId: s.clubId, seats: [] }; byId.set(s.gatheringId, g); groups.push(g); }
    byId.get(s.gatheringId).seats.push(s);
  }
  return (
    <div className="mb-10 space-y-6" data-testid="gathering-seats">
      <h2 className="font-mono text-xs font-bold uppercase tracking-widest text-gray-400">GATHERINGS · ENTRY SEATS</h2>
      {groups.map((g) => (
        <section key={g.key} className="border border-white/20" data-testid={`gathering-seats-${g.key}`}>
          <div className="flex items-center gap-4 border-b border-white/20 p-4">
            {g.coverUrl && <img src={g.coverUrl} alt="" referrerPolicy="no-referrer" className="h-16 w-16 flex-shrink-0 object-cover" />}
            <div className="min-w-0 flex-1">
              <h3 className="truncate font-mono text-base font-bold uppercase tracking-wider text-white">{g.name}</h3>
              <p className="font-mono text-xs text-gray-400">{formatDate(g.startsAt)} → {formatDate(g.endsAt)}</p>
            </div>
            <button type="button" onClick={() => navigate(g.clubId ? `/clubs/${g.clubId}/gatherings/${g.key}` : `/gatherings/${g.key}`)} className="flex-shrink-0 border border-white/40 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-white hover:bg-white hover:text-black">OPEN</button>
          </div>
          <div className="flex gap-4 overflow-x-auto p-4" style={{ scrollbarWidth: 'none' }}>
            {g.seats.map((s) => {
              const valid = s.status === 'paid' && !s.isFrozen;
              const label = s.checkedInAt ? 'CHECKED IN' : valid ? 'VALID' : String(s.status || '').toUpperCase().replace(/_/g, ' ');
              return (
                <div key={s.id} className="w-[85%] max-w-[300px] flex-shrink-0 cursor-pointer border border-white/20 bg-black text-white transition-colors hover:border-white" onClick={() => navigate(`/gt/${s.ticketCode}`)} data-testid={`seat-${s.id}`}>
                  <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
                    <p className="font-mono text-[10px] tracking-wider text-gray-500">SEAT {s.ticketNumber} OF {s.totalInBatch}{s.isTransferred && !s.viewerIsBuyer && <span className="ml-2 text-dreamPink">TRANSFERRED TO YOU</span>}</p>
                    <span className={`font-mono text-[10px] font-bold ${s.checkedInAt || valid ? 'text-green-400' : 'text-red-400'}`}>{label}</span>
                  </div>
                  <div className="flex flex-col items-center p-4">
                    <p className="mb-3 font-mono text-[10px] tracking-wider text-gray-500">GATHERING ENTRY</p>
                    {valid ? <img src={seatQrUrl(s.ticketCode).qr} alt="Seat QR" className="h-[140px] w-[140px]" /> : <div className="flex h-[140px] w-[140px] items-center justify-center border border-white/20 font-mono text-[10px] text-gray-600">NO QR</div>}
                    {s.pendingTransfer && <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-dreamPink">OFFER OPEN</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

function RailArrow({ side, disabled, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={side === 'left' ? 'Previous tickets' : 'More tickets'}
      className={`absolute top-1/2 hidden -translate-y-1/2 border border-white/40 bg-black/90 px-2 py-3 font-mono text-white transition-opacity sm:block ${
        side === 'left' ? 'left-1' : 'right-1'
      } ${disabled ? 'pointer-events-none opacity-0' : 'opacity-100 hover:bg-white hover:text-black'}`}
    >
      {side === 'left' ? '‹' : '›'}
    </button>
  );
}

function TicketCard({
  ticket, wallet, navigate,
  sendingTicketId, setSendingTicketId, sendForm, setSendForm, sendError, setSendError, setSendSuccess, onSend,
  transferTicketId, setTransferTicketId, onTransferChanged,
}) {
  const ticketUrl = `${webOrigin()}/ticket/${ticket.ticketCode}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(ticketUrl)}&size=200x200&bgcolor=000000&color=ffffff`;

  return (
    <div
      className="h-full cursor-pointer border border-white/20 bg-black text-white transition-colors hover:border-white"
      onClick={() => navigate(`/ticket/${ticket.ticketCode}`)}
    >
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
        <p className="font-mono text-[10px] tracking-wider text-gray-500">
          TICKET {ticket.ticketNumber} OF {ticket.totalInBatch}
          {ticket.wasTransferred && <span className="ml-2 text-dreamPink">TRANSFERRED TO YOU</span>}
        </p>
        {ticket.attendanceStatus === 'attended'
          ? <span className="font-mono text-[10px] font-bold text-green-400">CHECKED IN</span>
          : <span className="font-mono text-[10px] text-gray-500">NOT CHECKED IN</span>}
      </div>

      <div className="border-b border-white/10 px-4 py-3">
        <p className="font-mono text-[10px] tracking-wider text-gray-500">GUEST</p>
        <p className="truncate font-mono text-sm font-bold">{ticket.recipientName || ticket.buyerName || '—'}</p>
        {ticket.isSent && <span className="font-mono text-[10px] font-bold text-green-400">SENT</span>}
      </div>

      <div className="flex flex-col items-center p-4">
        <p className="mb-3 font-mono text-[10px] tracking-wider text-gray-500">SCAN FOR ENTRY</p>
        <img src={qrUrl} alt="QR Code" className="h-[140px] w-[140px]" />
        {(wallet.applePass || wallet.googlePass) && (
          <div className="mt-4 flex w-full flex-col gap-2" onClick={(e) => e.stopPropagation()}>
            {wallet.applePass && (
              <a href={`${API_BASE}/tickets/${ticket.ticketCode}/apple-pass`}
                className="w-full rounded-lg bg-white py-2.5 text-center font-mono text-xs font-bold tracking-wider text-black hover:opacity-90"> Add to Apple Wallet</a>
            )}
            {wallet.googlePass && (
              <a href={`${API_BASE}/tickets/${ticket.ticketCode}/google-pass`}
                className="w-full rounded-lg border border-white bg-black py-2.5 text-center font-mono text-xs font-bold tracking-wider text-white hover:opacity-90">Add to Google Wallet</a>
            )}
          </div>
        )}
      </div>

      <div className="flex gap-2 px-4 pb-4" onClick={(e) => e.stopPropagation()}>
        {!ticket.isSent && sendingTicketId !== ticket.id && (
          <button
            type="button"
            onClick={() => { setSendingTicketId(ticket.id); setSendError(''); setSendSuccess(''); }}
            className="flex-1 border border-white py-2 text-center font-mono text-[10px] font-bold uppercase tracking-wider transition-colors hover:bg-white hover:text-black"
          >
            SEND TO FRIEND
          </button>
        )}
      </div>

      {sendingTicketId === ticket.id && (
        <div className="space-y-2 px-4 pb-4" onClick={(e) => e.stopPropagation()}>
          <input
            type="email"
            placeholder="Recipient email"
            value={sendForm.email}
            onChange={(e) => setSendForm({ ...sendForm, email: e.target.value })}
            className="w-full border border-white/30 bg-transparent px-3 py-2 font-mono text-xs text-white placeholder-gray-500 outline-none focus:border-white"
          />
          <input
            placeholder="Name (optional)"
            value={sendForm.recipientName}
            onChange={(e) => setSendForm({ ...sendForm, recipientName: e.target.value })}
            className="w-full border border-white/30 bg-transparent px-3 py-2 font-mono text-xs text-white placeholder-gray-500 outline-none focus:border-white"
          />
          {sendError && <p className="font-mono text-[10px] text-red-400">{sendError}</p>}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onSend(ticket.id)}
              disabled={!sendForm.email}
              className="flex-1 bg-white py-2 font-mono text-[10px] font-bold uppercase tracking-wider text-black disabled:opacity-30"
            >
              SEND
            </button>
            <button
              type="button"
              onClick={() => { setSendingTicketId(null); setSendError(''); }}
              className="border border-white/30 px-4 py-2 font-mono text-[10px] text-gray-400 transition-colors hover:text-white"
            >
              CANCEL
            </button>
          </div>
        </div>
      )}

      <TransferPanel
        ticket={ticket}
        open={transferTicketId === ticket.id}
        onOpen={() => setTransferTicketId(ticket.id)}
        onClose={() => setTransferTicketId(null)}
        onChanged={onTransferChanged}
      />
    </div>
  );
}

/**
 * The transfer control, per ticket.
 *
 * Three states, and the card only ever shows one: a seat you can hand on, a
 * seat with an offer already out on it, and a seat that can't move (curated
 * event, still funding, already scanned, party's over) — which says why rather
 * than hiding the button, because "where did it go" is a support email and
 * "the host approves each guest" is not.
 */
function TransferPanel({ ticket, open, onOpen, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [email, setEmail] = useState('');
  const { showConfirm } = useDialog();

  const pending = ticket.pendingTransfer;

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Could not copy — long-press the link to copy it by hand.');
    }
  };

  const create = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await ticketService.createTransfer(ticket.id, { email: email.trim() || null });
      await copy(res.data.claimUrl);
      onClose();
      onChanged();
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Could not start the transfer.');
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    const ok = await showConfirm({
      title: 'Withdraw this offer?',
      message: 'The link stops working immediately. If you already sent it to someone, they will not be able to claim the ticket.',
      confirmLabel: 'WITHDRAW',
      cancelLabel: 'KEEP IT OPEN',
      variant: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await ticketService.cancelTransfer(pending.id);
      onChanged();
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Could not withdraw the offer.');
    } finally {
      setBusy(false);
    }
  };

  if (pending) {
    return (
      <div className="space-y-2 border-t border-white/10 px-4 py-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-dreamPink">
            Transfer pending
          </span>
          <span className="font-mono text-[10px] text-gray-600">Unclaimed</span>
        </div>
        <p className="break-all font-mono text-[10px] leading-relaxed text-gray-500">{pending.claimUrl}</p>
        {error && <p className="font-mono text-[10px] text-red-400">{error}</p>}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => copy(pending.claimUrl)}
            className="flex-1 border border-white py-2 font-mono text-[10px] font-bold uppercase tracking-wider transition-colors hover:bg-white hover:text-black"
          >
            {copied ? 'Copied' : 'Copy link'}
          </button>
          <button
            type="button"
            onClick={cancel}
            disabled={busy}
            className="border border-white/30 px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-gray-400 transition-colors hover:text-white disabled:opacity-40"
          >
            Withdraw
          </button>
        </div>
      </div>
    );
  }

  if (!ticket.canTransfer) {
    if (!ticket.transferBlockedReason) return null;
    return (
      <div className="border-t border-white/10 px-4 py-3" onClick={(e) => e.stopPropagation()}>
        <p className="font-mono text-[10px] leading-relaxed text-gray-600">{ticket.transferBlockedReason}</p>
      </div>
    );
  }

  if (!open) {
    return (
      <div className="border-t border-white/10 px-4 py-3" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={onOpen}
          className="w-full border border-white/40 py-2 font-mono text-[10px] font-bold uppercase tracking-wider transition-colors hover:bg-white hover:text-black"
        >
          Transfer ticket
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2 border-t border-white/10 px-4 py-3" onClick={(e) => e.stopPropagation()}>
      <p className="font-mono text-[10px] leading-relaxed text-gray-500">
        Makes a one-time link. Whoever opens it first takes the seat, and your QR stops working
        the moment they do. Sort out any money between yourselves — Portal never sees it.
      </p>
      <input
        type="email"
        placeholder="Email it too (optional)"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="w-full border border-white/30 bg-transparent px-3 py-2 font-mono text-xs text-white placeholder-gray-500 outline-none focus:border-white"
      />
      {error && <p className="font-mono text-[10px] text-red-400">{error}</p>}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={create}
          disabled={busy}
          className="flex-1 bg-white py-2 font-mono text-[10px] font-bold uppercase tracking-wider text-black disabled:opacity-30"
        >
          {busy ? 'Working…' : 'Create link'}
        </button>
        <button
          type="button"
          onClick={() => { onClose(); setError(''); }}
          className="border border-white/30 px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-gray-400 transition-colors hover:text-white"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

/**
 * Tickets → one block per event, soonest night first, tickets in batch order
 * inside a block. Exported for tests: this is where the page's shape is decided.
 */
export function groupByEvent(tickets) {
  const byEvent = new Map();
  for (const ticket of tickets) {
    // Identity is the event id. Title is a display label — two parties can share
    // one, and every orphaned ticket carries the same "Event removed".
    const key = ticket.eventId || `orphan:${ticket.id}`;
    if (!byEvent.has(key)) {
      byEvent.set(key, {
        key,
        eventId: ticket.eventId,
        eventTitle: ticket.eventTitle || 'Unknown Event',
        eventDateTime: ticket.eventDateTime,
        eventEndDateTime: ticket.eventEndDateTime,
        eventPosterUrl: ticket.eventPosterUrl,
        eventLocationCity: ticket.eventLocationCity,
        eventInviteCode: ticket.eventInviteCode,
        tickets: [],
      });
    }
    byEvent.get(key).tickets.push(ticket);
  }

  const groups = [...byEvent.values()];
  for (const group of groups) {
    group.tickets.sort((a, b) => (a.ticketNumber || 0) - (b.ticketNumber || 0));
  }
  // Soonest first. A group with no usable date sinks to the bottom rather than
  // jumping the queue — note null has to be caught before Date sees it, because
  // `new Date(null)` is 1970, not an invalid date, and would sort dead last-first.
  return groups.sort((a, b) => {
    const at = stamp(a.eventDateTime);
    const bt = stamp(b.eventDateTime);
    // Infinity - Infinity is NaN, which would make the comparator incoherent.
    if (at === bt) return 0;
    return at - bt;
  });
}

/** Milliseconds, or +Infinity for anything undateable. */
function stamp(value) {
  if (!value) return Number.POSITIVE_INFINITY;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
}

/**
 * When the night is actually over. Events that never set an end time get the
 * same 5-hour grace the backend's EventStatusService uses to end them, so a
 * ticket doesn't drop into the past section while the party is still going.
 */
const GRACE_MS = 5 * 60 * 60 * 1000;
export function eventEndsAt(group) {
  const end = stamp(group.eventEndDateTime);
  if (end !== Number.POSITIVE_INFINITY) return end;
  const start = stamp(group.eventDateTime);
  return start === Number.POSITIVE_INFINITY ? Number.POSITIVE_INFINITY : start + GRACE_MS;
}

/**
 * Split the grouped tickets into what's still ahead of you and what's done.
 *
 * Upcoming runs soonest first — the next door you walk through is the one you
 * need at the top. Past runs most recent first, because the further back it
 * goes the less you care. An undateable group (its event row is gone) counts as
 * upcoming: it sinks to the bottom there, where it's still reachable, instead
 * of being buried in a collapsed section you'd never think to open.
 */
export function splitByRecency(tickets, now = Date.now()) {
  const groups = groupByEvent(tickets);
  const upcoming = [];
  const past = [];
  for (const group of groups) {
    if (eventEndsAt(group) < now) past.push(group); else upcoming.push(group);
  }
  past.sort((a, b) => stamp(b.eventDateTime) - stamp(a.eventDateTime));
  return { upcoming, past };
}
